import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Delete,
  DragIndicator,
  ExpandLess,
  ExpandMore,
  VisibilityOff,
} from '@mui/icons-material';
import { useAssessmentMutations } from '../../../hooks/assessment-hooks';
import {
  accountLookupQuestions,
  AssessmentQuestion,
  AssessmentQuestionOption,
  AssessmentResponseType,
  CHOICE_RESPONSE_TYPES,
  PREVIEW_UNSAFE_RESPONSE_TYPES,
  RESPONSE_TYPES,
  demoAnswer,
  normalizeOptions,
  VisibleWhen,
} from '../../../support/assessments';
import AssessmentQuestionInput from '../../Assessments/AssessmentQuestionInput';
import InlineEditableText from './InlineEditableText';

/**
 * One question in the builder, edited entirely in place — no modal.
 *
 * The row itself carries the drag handle, the question text (inline), its response type and the
 * required toggle. Everything heavier — help text, weight, choice options, scale bounds and the
 * `visible_when` rule — lives in an expandable panel below the row. Each control commits its own
 * partial write as it changes, so there is no Save button; `putItem` only touches the field(s) sent.
 *
 * `key` and `order` are intentionally not shown: the key was auto-assigned at creation and stays
 * fixed (conditional rules reference it), and order is set by dragging.
 */

type Props = {
  question: AssessmentQuestion;
  /** Every question in the assessment, so a `visible_when` rule can reference any other one. */
  allQuestions: AssessmentQuestion[];
  /** Start with the config panel open — set for a just-added question so the author edits it now. */
  defaultExpanded?: boolean;
  onDelete: () => void;
};

type ScaleOptions = { min: number; max: number; minLabel: string; maxLabel: string };

const DEFAULT_SCALE: ScaleOptions = { min: 1, max: 5, minLabel: '', maxLabel: '' };

const scaleFrom = (raw: any): ScaleOptions => ({
  min: Number.isFinite(Number(raw?.min)) ? Number(raw?.min) : 1,
  max: Number.isFinite(Number(raw?.max)) ? Number(raw?.max) : 5,
  minLabel: raw?.minLabel ?? '',
  maxLabel: raw?.maxLabel ?? '',
});

const AssessmentQuestionRow: React.FC<Props> = ({
  question,
  allQuestions,
  defaultExpanded = false,
  onDelete,
}) => {
  const { update } = useAssessmentMutations('question');
  const [expanded, setExpanded] = useState(defaultExpanded);

  const [options, setOptions] = useState<AssessmentQuestionOption[]>([]);
  const [scale, setScale] = useState<ScaleOptions>(DEFAULT_SCALE);
  const [conditional, setConditional] = useState(false);
  const [condQuestion, setCondQuestion] = useState('');
  const [condMode, setCondMode] = useState<'equals' | 'answered'>('equals');
  const [condValue, setCondValue] = useState('');

  // The sample answer shown in the live "Preview" box. Re-seeded only when the response type changes
  // (below) — not on every option/label keystroke, so editing options doesn't fight the admin's own
  // interaction with the preview input.
  const [demoVal, setDemoVal] = useState<any>(() => demoAnswer(question));

  useEffect(() => {
    setOptions(normalizeOptions(question.response_options));
    setScale(scaleFrom(question.response_options));
    const vw = question.visible_when as VisibleWhen | null | undefined;
    setConditional(!!vw?.question);
    setCondQuestion(vw?.question ?? '');
    setCondMode(vw?.notEmpty ? 'answered' : 'equals');
    setCondValue(vw?.equals != null ? String(vw.equals) : '');
  }, [question]);

  // Re-seed the preview answer when the type changes (a stale option value or a text string on a
  // now-numeric field would render oddly). Keyed on type + id only, so it survives option edits.
  useEffect(() => {
    setDemoVal(demoAnswer(question));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.response_type, question.id]);

  const isChoice = CHOICE_RESPONSE_TYPES.includes(question.response_type);
  const isScale = question.response_type === 'scale';
  const isAccountFile = question.response_type === 'sf_account_file';

  // The `sf_account` questions this file question could attach its upload to. It cannot itself be
  // one, so no self-exclusion is needed.
  const accountSources = useMemo(
    () => accountLookupQuestions(allQuestions),
    [allQuestions]
  );

  const conditionSources = useMemo(
    () => allQuestions.filter((q) => q.id !== question.id && q.key),
    [allQuestions, question.id]
  );

  // A PUT on the generic runtime is a full update — every required field (section, key, text) must
  // be present or it 400s "This field is required." So send the whole question with the patch on
  // top, not just the changed field.
  const commit = (patch: Record<string, any>) => update.mutate({ ...question, ...patch });

  const optionsToPayload = (list: AssessmentQuestionOption[]) =>
    list
      .map((opt) => ({
        value: (opt.value || opt.label || '').trim(),
        label: (opt.label || opt.value || '').trim(),
      }))
      .filter((opt) => opt.value);

  // The question as the preview should render it — the committed record with the in-progress option
  // and scale edits merged on top, so tweaking a choice label or a scale bound updates the preview
  // immediately (before the debounced commit / refetch lands).
  const previewQuestion = useMemo<AssessmentQuestion>(() => {
    if (isChoice) return { ...question, response_options: optionsToPayload(options) };
    if (isScale)
      return {
        ...question,
        response_options: {
          min: scale.min,
          max: scale.max,
          ...(scale.minLabel ? { minLabel: scale.minLabel } : {}),
          ...(scale.maxLabel ? { maxLabel: scale.maxLabel } : {}),
        },
      };
    return question;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, isChoice, isScale, options, scale]);

  const previewDisabled = PREVIEW_UNSAFE_RESPONSE_TYPES.includes(question.response_type);

  const commitOptions = (list: AssessmentQuestionOption[]) =>
    commit({ response_options: optionsToPayload(list) });

  const commitScale = (next: ScaleOptions) =>
    commit({
      response_options: {
        min: next.min,
        max: next.max,
        ...(next.minLabel ? { minLabel: next.minLabel } : {}),
        ...(next.maxLabel ? { maxLabel: next.maxLabel } : {}),
      },
    });

  const buildVisibleWhen = (
    on: boolean,
    q: string,
    mode: 'equals' | 'answered',
    val: string
  ): VisibleWhen | null => {
    if (!on || !q) return null;
    if (mode === 'answered') return { question: q, notEmpty: true };
    return { question: q, equals: val };
  };

  const commitCondition = (
    on: boolean,
    q: string,
    mode: 'equals' | 'answered',
    val: string
  ) => commit({ visible_when: buildVisibleWhen(on, q, mode, val) });

  const handleTypeChange = (nextType: AssessmentResponseType) => {
    // Reset the JSON options to match the new type so a stale option list can't linger on, say, a
    // question switched from single_choice to text.
    const nextIsChoice = CHOICE_RESPONSE_TYPES.includes(nextType);
    const nextIsScale = nextType === 'scale';
    // response_options is JSONField(default=dict, blank=True) — NOT nullable — so an unused options
    // slot must be {} (the model default), never null, or the generic CRUD create/update 400s.
    let response_options: any = {};
    if (nextIsChoice) response_options = optionsToPayload(options);
    else if (nextIsScale) response_options = { min: scale.min, max: scale.max };
    commit({ response_type: nextType, response_options });
  };

  return (
    <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'background.default' }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title="Drag to reorder">
          <DragIndicator
            className="question-drag-handle"
            fontSize="small"
            sx={{ cursor: 'grab', color: 'text.disabled' }}
          />
        </Tooltip>

        <Box flex={1} minWidth={0}>
          <InlineEditableText
            value={question.text}
            placeholder="Question text"
            onCommit={(text) => commit({ text })}
          />
        </Box>

        <TextField
          select
          size="small"
          value={question.response_type}
          onChange={(e) => handleTypeChange(e.target.value as AssessmentResponseType)}
          sx={{ minWidth: 150 }}
        >
          {RESPONSE_TYPES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Tooltip title="Required">
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={!!question.required}
                onChange={(e) => commit({ required: e.target.checked })}
              />
            }
            label={<Typography variant="caption">Req</Typography>}
          />
        </Tooltip>

        {question.visible_when && (
          <Tooltip title="Shown conditionally">
            <VisibilityOff fontSize="small" color="action" />
          </Tooltip>
        )}

        <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={onDelete}>
          <Delete fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Divider sx={{ my: 1 }} />
        <Box display="grid" gap={2} px={1} pb={1}>
          <InlineEditableText
            value={question.help_text}
            placeholder="Help text (optional)"
            onCommit={(help_text) => commit({ help_text })}
          />

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Weight (optional)"
              type="number"
              size="small"
              defaultValue={question.weight != null ? String(question.weight) : ''}
              helperText="Used by scored assessments"
              onBlur={(e) =>
                commit({ weight: e.target.value.trim() === '' ? null : Number(e.target.value) })
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={question.active !== false}
                  onChange={(e) => commit({ active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>

          {isChoice && (
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">Options</Typography>
                <IconButton
                  size="small"
                  onClick={() => setOptions((prev) => [...prev, { value: '', label: '' }])}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
              {options.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No options yet.
                </Typography>
              )}
              {options.map((opt, index) => (
                <Box key={index} display="flex" gap={1} alignItems="center" mt={1}>
                  <TextField
                    size="small"
                    label="Value"
                    value={opt.value}
                    onChange={(e) =>
                      setOptions((prev) =>
                        prev.map((o, i) => (i === index ? { ...o, value: e.target.value } : o))
                      )
                    }
                    onBlur={() => commitOptions(options)}
                  />
                  <TextField
                    size="small"
                    label="Label"
                    fullWidth
                    value={opt.label}
                    onChange={(e) =>
                      setOptions((prev) =>
                        prev.map((o, i) => (i === index ? { ...o, label: e.target.value } : o))
                      )
                    }
                    onBlur={() => commitOptions(options)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const next = options.filter((_o, i) => i !== index);
                      setOptions(next);
                      commitOptions(next);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {isScale && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <TextField
                label="Min"
                type="number"
                size="small"
                value={scale.min}
                onChange={(e) => setScale((p) => ({ ...p, min: Number(e.target.value) }))}
                onBlur={() => commitScale(scale)}
              />
              <TextField
                label="Max"
                type="number"
                size="small"
                value={scale.max}
                onChange={(e) => setScale((p) => ({ ...p, max: Number(e.target.value) }))}
                onBlur={() => commitScale(scale)}
              />
              <TextField
                label="Min Label (optional)"
                size="small"
                value={scale.minLabel}
                onChange={(e) => setScale((p) => ({ ...p, minLabel: e.target.value }))}
                onBlur={() => commitScale(scale)}
              />
              <TextField
                label="Max Label (optional)"
                size="small"
                value={scale.maxLabel}
                onChange={(e) => setScale((p) => ({ ...p, maxLabel: e.target.value }))}
                onBlur={() => commitScale(scale)}
              />
            </Box>
          )}

          {isAccountFile && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Salesforce account
              </Typography>
              {accountSources.length === 0 ? (
                <Alert severity="warning">
                  Add a “Salesforce Account” question to this assessment first — the uploaded file
                  attaches to the account chosen there.
                </Alert>
              ) : accountSources.length === 1 ? (
                <Typography variant="body2" color="text.secondary">
                  Files attach to the account chosen in “
                  {accountSources[0].text || accountSources[0].key}”.
                </Typography>
              ) : (
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Attach files to the account from"
                  value={(question.response_options?.accountSource as string) || ''}
                  helperText="Which Salesforce Account question this upload uses."
                  onChange={(e) => commit({ response_options: { accountSource: e.target.value } })}
                >
                  {accountSources.map((q) => (
                    <MenuItem key={q.id} value={q.key}>
                      {q.text || q.key}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          )}

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={conditional}
                  disabled={conditionSources.length === 0}
                  onChange={(e) => {
                    setConditional(e.target.checked);
                    commitCondition(e.target.checked, condQuestion, condMode, condValue);
                  }}
                />
              }
              label="Show only when another answer matches"
            />
            {conditionSources.length === 0 && (
              <Typography variant="caption" color="text.secondary" display="block">
                Add other questions first to make this one conditional.
              </Typography>
            )}
            {conditional && conditionSources.length > 0 && (
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={1}>
                <TextField
                  select
                  size="small"
                  label="Depends on"
                  value={condQuestion}
                  onChange={(e) => {
                    setCondQuestion(e.target.value);
                    commitCondition(conditional, e.target.value, condMode, condValue);
                  }}
                >
                  {conditionSources.map((q) => (
                    <MenuItem key={q.id} value={q.key}>
                      {q.text || q.key}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Condition"
                  value={condMode}
                  onChange={(e) => {
                    const mode = e.target.value as 'equals' | 'answered';
                    setCondMode(mode);
                    commitCondition(conditional, condQuestion, mode, condValue);
                  }}
                >
                  <MenuItem value="equals">Equals</MenuItem>
                  <MenuItem value="answered">Is answered</MenuItem>
                </TextField>
                {condMode === 'equals' && (
                  <TextField
                    size="small"
                    label="Value"
                    value={condValue}
                    helperText="For yes/no use true or false"
                    onChange={(e) => setCondValue(e.target.value)}
                    onBlur={() => commitCondition(conditional, condQuestion, condMode, condValue)}
                  />
                )}
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              How this question appears to a respondent, with a sample answer.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" component="label">
                {previewQuestion.text || 'Question text'}
                {previewQuestion.required && (
                  <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                    *
                  </Typography>
                )}
              </Typography>
              {!!previewQuestion.help_text && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {previewQuestion.help_text}
                </Typography>
              )}
              <Box mt={1}>
                <AssessmentQuestionInput
                  question={previewQuestion}
                  value={demoVal}
                  onChange={setDemoVal}
                  disabled={previewDisabled}
                />
              </Box>
              {previewDisabled && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Disabled in preview — this input creates data or calls an external service at fill
                  time.
                </Typography>
              )}
            </Paper>
          </Box>

          <Box>
            <Chip size="small" variant="outlined" label={`key: ${question.key}`} />
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AssessmentQuestionRow;
